import { useEffect, useRef, useState } from 'react';
import { useAppContext } from '../../context/AppContext';

// Sugestões de categorias comuns
const EXPENSE_CATEGORIES = [
    { icon: '⚡', name: 'Energia elétrica', type: 'fixa' },
    { icon: '💧', name: 'Água', type: 'fixa' },
    { icon: '🔥', name: 'Gás', type: 'variavel' },
    { icon: '📦', name: 'Embalagens', type: 'variavel' },
    { icon: '🧾', name: 'Aluguel', type: 'fixa' },
    { icon: '📱', name: 'Internet/Telefone', type: 'fixa' },
    { icon: '🛒', name: 'Matéria-prima', type: 'variavel' },
    { icon: '🚗', name: 'Transporte/Entrega', type: 'variavel' },
    { icon: '🧹', name: 'Limpeza', type: 'variavel' },
    { icon: '👤', name: 'Salários', type: 'fixa' },
    { icon: '📄', name: 'Impostos', type: 'fixa' },
    { icon: '🔧', name: 'Manutenção', type: 'variavel' },
];

export default function ExpenseForm() {
    const { addExpense, expenses } = useAppContext();
    const [description, setDescription] = useState('');
    const [type, setType] = useState('variavel');
    const [amount, setAmount] = useState('');
    const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');

    const descRef = useRef(null);
    const amountRef = useRef(null);

    useEffect(() => {
        if (descRef.current) descRef.current.focus();
    }, []);

    // Histórico de despesas recentes (últimas 5 únicas)
    const recentDescriptions = [...new Set(expenses.map(e => e.description))].slice(0, 5);

    const selectCategory = (cat) => {
        setDescription(cat.name);
        setType(cat.type);
        setShowSuggestions(false);
        if (amountRef.current) amountRef.current.focus();
    };

    const selectRecent = (desc) => {
        const recentExpense = expenses.find(e => e.description === desc);
        setDescription(desc);
        if (recentExpense) {
            setType(recentExpense.type);
        }
        setShowSuggestions(false);
        if (amountRef.current) amountRef.current.focus();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        if (!description.trim() || !amount) return;

        try {
            setSaving(true);
            await addExpense({
                description: description.trim(),
                type,
                amount: Number(amount),
                date,
            });

            setDescription('');
            setAmount('');
            setType('variavel');
            if (descRef.current) descRef.current.focus();
            setSuccess('✓ Despesa registrada!');
            setTimeout(() => setSuccess(''), 2500);
        } catch (err) {
            setError('Não foi possível registrar a despesa. Tente novamente.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="card shadow-sm">
            <div className="card-body">
                <h2 className="h5 mb-3">💸 Registrar Despesa</h2>

                {success && <div className="alert alert-success py-2 small">{success}</div>}
                {error && <div className="alert alert-danger py-2 small">{error}</div>}

                {/* Categorias rápidas */}
                <div className="mb-3">
                    <label className="form-label fw-semibold small">Categorias rápidas:</label>
                    <div className="row g-2">
                        {EXPENSE_CATEGORIES.slice(0, 6).map((cat) => (
                            <div key={cat.name} className="col-6">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary w-100"
                                    onClick={() => selectCategory(cat)}
                                    title={`${cat.name} (${cat.type})`}
                                >
                                    {cat.icon} {cat.name}
                                </button>
                            </div>
                        ))}
                        <div className="col-12">
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary w-100"
                                onClick={() => setShowSuggestions(!showSuggestions)}
                            >
                                {showSuggestions ? '▲ Menos' : '▼ Mais'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mais categorias e histórico */}
                {showSuggestions && (
                    <div className="mb-3 p-2 bg-light rounded border">
                        <div className="small fw-semibold mb-2">Todas as categorias:</div>
                        <div className="row g-2 mb-3">
                            {EXPENSE_CATEGORIES.slice(6).map((cat) => (
                                <div key={cat.name} className="col-6">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary w-100"
                                        onClick={() => selectCategory(cat)}
                                    >
                                        {cat.icon} {cat.name}
                                    </button>
                                </div>
                            ))}
                        </div>

                        {recentDescriptions.length > 0 && (
                            <>
                                <div className="small fw-semibold mb-2">Recentes:</div>
                                <div className="row g-2">
                                    {recentDescriptions.map((desc) => (
                                        <div key={desc} className="col-6">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-info w-100"
                                                onClick={() => selectRecent(desc)}
                                            >
                                                🕒 {desc}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}

                <div className="mb-3">
                    <label className="form-label fw-semibold">Descrição</label>
                    <input
                        ref={descRef}
                        type="text"
                        className="form-control"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Ex: Gás, energia, embalagem..."
                        required
                    />
                </div>

                <div className="row">
                    <div className="col-5 mb-3">
                        <label className="form-label fw-semibold">Tipo</label>
                        <select
                            className="form-select"
                            value={type}
                            onChange={(e) => setType(e.target.value)}
                        >
                            <option value="variavel">💹 Variável</option>
                            <option value="fixa">📌 Fixa</option>
                        </select>
                        <div className="form-text small">
                            {type === 'fixa' ? 'Todo mês' : 'Conforme uso'}
                        </div>
                    </div>

                    <div className="col-7 mb-3">
                        <label className="form-label fw-semibold">Valor</label>
                        <div className="input-group input-group-lg">
                            <span className="input-group-text">R$</span>
                            <input
                                ref={amountRef}
                                type="number"
                                min="0"
                                step="0.01"
                                className="form-control"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                placeholder="0,00"
                                required
                            />
                        </div>
                    </div>
                </div>

                <div className="mb-3">
                    <label className="form-label fw-semibold">Data</label>
                    <input
                        type="date"
                        className="form-control"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        required
                    />
                </div>

                {/* Preview */}
                {description && amount && (
                    <div className="mb-3 p-2 bg-danger bg-opacity-10 rounded border border-danger">
                        <div className="d-flex justify-content-between align-items-center">
                            <span className="small">
                                <strong>{description}</strong> ({type === 'fixa' ? 'Fixa' : 'Variável'})
                            </span>
                            <span className="fw-bold text-danger">
                                - R$ {Number(amount).toFixed(2)}
                            </span>
                        </div>
                    </div>
                )}

                <button
                    type="submit"
                    className="btn btn-danger w-100"
                    disabled={saving}
                >
                    {saving ? '⏳ Registrando...' : '✓ Registrar Despesa'}
                </button>
            </div>
        </form>
    );
}
